/**
 * useMusicGeneration - Delegates job lifecycle to GenerationContext so that
 * progress + result survive tab switches and page reloads.
 *
 * Extends the basic config with custom-lyrics support (Suno-style workflow):
 * when useCustomLyrics is true the lyrics text is embedded directly into the
 * Lyria prompt before submission.
 */

import { useState, useCallback, useEffect } from 'react';
import { musicGenerationAPI } from '../services/musicGenerationAPI';
import type { MusicModel } from '../services/musicGenerationAPI';
import type { MusicJobResult } from '../services/polling/musicPollAdapter';
import { audioNotification } from '../utils/audioNotification';
import { notifyTokenBalanceChanged } from './useTokenBalance';
import { handleError } from '../utils/errorHandler';
import { useGeneration } from '../contexts/GenerationContext.context';
import { useGenerationJob } from './useGenerationJob';
import { useTranslation } from './useTranslation';

// Expected generation time estimates per model (seconds)
const MODEL_EXPECTED_SECONDS: Record<string, number> = {
  'lyria-clip': 25,
  'lyria-pro': 60,
};
const DEFAULT_EXPECTED_SECONDS = 30;

export interface MusicGenerationConfig {
  prompt: string;
  model: string;
  format: 'mp3';
}

export interface UseMusicGenerationReturn {
  config: MusicGenerationConfig;
  setConfig: React.Dispatch<React.SetStateAction<MusicGenerationConfig>>;
  isGenerating: boolean;
  progress: number;
  startedAt: string | null;
  expectedSeconds: number;
  result: MusicJobResult | null;
  /** Job ID of the most recent completed music generation job. Null while generating or before first job. */
  currentJobId: string | null;
  error: string | null;
  models: MusicModel[];
  generateMusic: () => Promise<void>;
  reset: () => void;
  // Custom-lyrics workflow
  useCustomLyrics: boolean;
  setUseCustomLyrics: React.Dispatch<React.SetStateAction<boolean>>;
  lyrics: string;
  setLyrics: React.Dispatch<React.SetStateAction<string>>;
  isDraftingLyrics: boolean;
  lyricsError: string | null;
  draftLyrics: () => Promise<void>;
}

const DEFAULT_CONFIG: MusicGenerationConfig = {
  prompt: '',
  model: 'lyria-clip',
  format: 'mp3',
};

export function useMusicGeneration(): UseMusicGenerationReturn {
  const { startJob } = useGeneration();
  const { t } = useTranslation();

  const [config, setConfig] = useState<MusicGenerationConfig>(DEFAULT_CONFIG);
  const [startError, setStartError] = useState<string | null>(null);
  const [models, setModels] = useState<MusicModel[]>([]);

  // Custom-lyrics state
  const [useCustomLyrics, setUseCustomLyrics] = useState<boolean>(false);
  const [lyrics, setLyrics] = useState<string>('');
  const [isDraftingLyrics, setIsDraftingLyrics] = useState<boolean>(false);
  const [lyricsError, setLyricsError] = useState<string | null>(null);

  // Fetch available models once on mount
  useEffect(() => {
    musicGenerationAPI
      .listModels()
      .then((response) => {
        if (response.success && response.data) {
          setModels(response.data.models);
          // Default to first model if available
          if (response.data.models.length > 0 && response.data.models[0]) {
            setConfig((prev) => ({ ...prev, model: response.data!.models[0]!.id }));
          }
        }
      })
      .catch(() => {
        // Models unavailable — keep default config
      });
  }, []);

  const expectedSeconds = MODEL_EXPECTED_SECONDS[config.model] ?? DEFAULT_EXPECTED_SECONDS;

  const { isGenerating, progress, startedAt, result, currentJobId, jobError, clearJobs } =
    useGenerationJob<MusicJobResult>('music', { expectedSeconds });

  const error = startError ?? jobError;

  /**
   * Draft lyrics from the current prompt using the LLM cascade.
   * Requires a non-empty prompt — fails loudly otherwise.
   */
  const draftLyrics = useCallback(async (): Promise<void> => {
    if (!config.prompt.trim()) {
      setLyricsError(t('music.form.lyricsErrorEmptyPrompt'));
      return;
    }

    setLyricsError(null);
    setIsDraftingLyrics(true);

    try {
      const drafted = await musicGenerationAPI.draftLyrics(config.prompt);
      setLyrics(drafted);
    } catch (err) {
      const msg = handleError(err, 'MusicLyricsDraft', 'Failed to draft lyrics');
      setLyricsError(msg);
    } finally {
      setIsDraftingLyrics(false);
    }
  }, [config.prompt, t]);

  const generateMusic = useCallback(async (): Promise<void> => {
    if (!config.prompt.trim()) {
      setStartError('Please enter a prompt');
      return;
    }

    setStartError(null);

    // Build the effective prompt — embed lyrics when custom-lyrics mode is on
    const effectivePrompt =
      useCustomLyrics && lyrics.trim()
        ? `${config.prompt}\n\nUse these exact lyrics:\n${lyrics}`
        : config.prompt;

    try {
      const response = await musicGenerationAPI.generateMusic({
        ...config,
        prompt: effectivePrompt,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Failed to start music generation');
      }

      const data = response.data;

      if (data.job_id) {
        startJob<MusicGenerationConfig>({
          id: data.job_id,
          tabMode: 'music',
          serverType: 'generation',
          metadata: config,
          title: 'Music generation',
        });
        notifyTokenBalanceChanged();
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (err) {
      const errorMessage = handleError(err, 'MusicGeneration', 'An error occurred');
      setStartError(errorMessage);
      notifyTokenBalanceChanged();
      audioNotification.playError().catch(() => {});
    }
  }, [config, useCustomLyrics, lyrics, startJob]);

  const reset = useCallback((): void => {
    setStartError(null);
    setConfig(DEFAULT_CONFIG);
    setLyrics('');
    setLyricsError(null);
    setUseCustomLyrics(false);
    clearJobs();
  }, [clearJobs]);

  return {
    config,
    setConfig,
    isGenerating,
    progress,
    startedAt,
    expectedSeconds,
    result,
    currentJobId,
    error,
    models,
    generateMusic,
    reset,
    useCustomLyrics,
    setUseCustomLyrics,
    lyrics,
    setLyrics,
    isDraftingLyrics,
    lyricsError,
    draftLyrics,
  };
}

export default useMusicGeneration;
